import { ValidationErrorsMap } from '@v2/states/validation';
import { AsyncLoadable, AsyncLoadableStatus } from '@v2/utils/mobx/async-loadable';
import { makeAutoObservable, reaction } from 'mobx';
import { diContainer } from 'shared/infrastructure/di-container';
import { ProviderClient, ProviderFactory } from 'shared/providers/provider';
import { DataField, DataObject } from 'shared/providers/types';
import { FilterCondition, ProviderType, PullMode } from 'shared/types/flowstep';

const getObjectTypeValidationMessage = (provider: ProviderType) => {
  switch (provider) {
    case ProviderType.HubspotList:
      return 'List must be selected';
    default:
      return 'Object type must be selected';
  }
};

export class PullSettingsState {
  createNewSheet = true;
  _selectedObject: DataObject | undefined = undefined;
  selectedFieldNames: string[] = [];
  conditions: FilterCondition[] = [];
  pullMode: PullMode = PullMode.OVERWRITE;
  withDateTimeColumn = true;
  provider: ProviderClient;

  _selectedObjectId: string | undefined = undefined;
  objectLists = new AsyncLoadable<DataObject[]>([]);
  objectFields = new AsyncLoadable<DataField[]>([]);

  private loadObjectFields() {
    this.objectFields.load(async () => {
      if (this._selectedObject) {
        return await this.provider.loadObjectFields(this._selectedObject.id || this._selectedObject.name);
      }
      return [];
    });
  }

  constructor(private providerType: ProviderType) {
    makeAutoObservable(this);
    const providerFactory = diContainer.resolve<ProviderFactory>('providerFactory');
    this.provider = providerFactory.getProviderClient(providerType);
    this.objectLists.load(async () => this.provider.loadObjectTypes());

    reaction(
      () => [this._selectedObjectId, this.objectLists.status, this.objectLists.data],
      ([objectId, status]) => {
        if (status === AsyncLoadableStatus.ready && objectId) {
          this._selectedObject = this.objectLists.data.find((object) => object.id === objectId);
        }
      },
    );

    reaction(
      () => this._selectedObject,
      () => this.loadObjectFields(),
    );
  }

  loadObjectTypes(options?: any) {
    this.objectLists.load(async () => await this.provider.loadObjectTypes(options));
  }

  validate(errors: ValidationErrorsMap<string>): void {
    if (!this.selectedObject) {
      errors.set('object-type-not-selected', getObjectTypeValidationMessage(this.providerType));
    }

    if (this.selectedFieldNames.length === 0) {
      errors.set('selected-fields-empty', 'At least one field must be selected');
    }
  }

  get selectedObject() {
    return this._selectedObject;
  }

  set selectedObject(value: DataObject | undefined) {
    this._selectedObject = value;
  }

  get objectLoadingError() {
    return this.objectLists.error;
  }

  get objectFieldsLoadingError() {
    return this.objectFields.error;
  }

  set selectedObjectId(id: string) {
    if (this._selectedObjectId !== id) {
      this.selectedFieldNames = [];
    }
    this._selectedObjectId = id;
  }

  get selectedObjectId(): string | undefined {
    return this._selectedObject ? this._selectedObject.id : this._selectedObjectId;
  }
}

export const pullModes = [
  {
    key: PullMode.APPEND,
    text: 'Append',
    description:
      'On refresh, keeps originally pulled data in place and appends the all pull results at the end of ' +
      'the existing data. May cause duplicate rows. Make sure you pull a Date or DateTime column to be able to ' +
      'distinguish between the older and newer results.',
  },
  {
    key: PullMode.NEW_SHEET,
    text: 'New Sheet',
    description:
      'Every refresh will create a separate timestamped worksheet with the pull results. ' +
      'The original data will stay intact. Good for keeping track of historical changes.',
  },
  {
    key: PullMode.OVERWRITE,
    text: 'Overwrite',
    description: 'On refresh, overwrites the data in the range where it was initially pulled.',
  },
];
